import { AlertTriangle, BookOpenCheck, Clipboard, Eye, MessageSquareText, RefreshCw, Save, Send, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import RecipientEditor from "./RecipientEditor";
import { Button } from "../../components/ui/Button";
import { EmptyState, ErrorState, LoadingState } from "../../components/ui/Feedback";
import { TextArea } from "../../components/ui/Form";
import { PROCESSING_STATUSES } from "../../lib/statuses";
import { splitEmails } from "../../lib/formatters";

function stripVisibleSignature(value, configuredSignature) {
  let next = String(value || "").trim();
  const signature = String(configuredSignature || "").trim();
  if (signature && next.endsWith(signature)) next = next.slice(0, -signature.length).trimEnd();
  const lines = next.split(/\r?\n/);
  const closingPattern = /^(?:thanks?\s*(?:&|and)?\s*)?(?:best|kind|warm)\s+regards[,.!]*$|^(?:sincerely|respectfully)[,.!]*$/i;
  const closingIndex = lines.findIndex((line, index) => index >= Math.max(1, lines.length - 9) && closingPattern.test(line.trim()));
  if (closingIndex > 0) next = lines.slice(0, closingIndex).join("\n").trimEnd();
  return next;
}

const REVIEW_WARNING_LABELS = {
  prompt_or_reasoning_leak: "草稿可能包含内部生成说明，请删除后再发送",
  internal_research_leak: "草稿可能向客户暴露内部背调内容",
  customer_facing_risk_language: "草稿包含不适合直接向客户表达的风险判断",
  unapproved_business_claim: "草稿中的认证、库存、交期、付款或质保承诺需要核对",
  unsupported_product_fact: "产品参数缺少已审核知识依据，请人工核对",
  unsupported_product_fact_combination: "多项产品参数未能在同一份已审核资料中交叉确认",
  missing_salutation: "缺少合适的客户称呼",
  missing_good_day_thanks: "缺少 Good day 或感谢语",
  missing_direct_response: "尚未直接回应客户的核心需求",
  missing_next_action: "缺少客户回复后的明确下一步行动",
  missing_cooperation_closing: "缺少符合客户场景的合作期待收尾",
  too_many_questions: "问题数量较多，建议减少客户回复负担",
  question_count_out_of_policy: "提问数量与当前客户等级策略不一致",
  length_out_of_policy: "草稿篇幅与当前客户等级策略不一致",
  non_english_reply: "草稿中可能包含非英文内容",
  first_contact_price_quote: "首封回复可能包含未经审核的价格信息",
  manual_review_required: "客户原文信息不足，需要人工判断后发送",
  follow_up_auto_reply_disabled: "客户后续来信已保留草稿，不会自动发送；请结合历史往来人工确认后发送",
};

function warningText(item) {
  return REVIEW_WARNING_LABELS[item?.type] || item?.message || "该草稿需要人工核对";
}

export default function ReplyComposer({
  inquiry, settings, suggestions, onGenerate, onSave, onSend, onRecipientsSave, onPreview, busy,
  secondaryLoading = false,
  secondaryError = "",
}) {
  const [draft, setDraft] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [cc, setCc] = useState([]);
  const [instruction, setInstruction] = useState("");
  const [dirty, setDirty] = useState(false);
  const [copied, setCopied] = useState(false);
  const sending = ["send_queued", "sending"].includes(inquiry.status);
  const sent = inquiry.status === "replied";
  const working = (!sending && PROCESSING_STATUSES.has(inquiry.status)) || busy === "generate";
  const reviewWarnings = Array.isArray(inquiry.reply_validation_warnings) ? inquiry.reply_validation_warnings : [];

  useEffect(() => {
    const next = stripVisibleSignature(inquiry.ai_draft, settings?.reply_signature);
    setDraft(next);
    setRecipients(splitEmails(inquiry.recipient_emails?.length ? inquiry.recipient_emails : inquiry.reply_recipient_email || inquiry.sender_email));
    setCc(splitEmails(inquiry.cc_emails));
    setDirty(false);
    setInstruction("");
  }, [inquiry.id, inquiry.ai_draft, inquiry.recipient_emails, inquiry.reply_recipient_email, inquiry.sender_email, inquiry.cc_emails, settings?.reply_signature]);

  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (event) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const changeRecipients = async (next) => {
    setRecipients(next);
    await onRecipientsSave(next);
  };
  return (
    <section className="reply-composer">
      <header className="ai-panel-header">
        <div><span><MessageSquareText size={16} />英文回复草稿</span><small>AI 生成内容仅供审核，签名会在预览和发送时附加。</small></div>
        <Button size="sm" variant="ghost" icon={Eye} disabled={!draft.trim()} onClick={() => onPreview({ draft, recipients, cc })}>发送预览</Button>
      </header>
      <div className="reply-addresses">
        <RecipientEditor label="收件人" emails={recipients} onChange={changeRecipients} />
        <RecipientEditor label="抄送" emails={cc} onChange={setCc} suggestions={suggestions} />
      </div>
      {sending ? <LoadingState label="邮件已提交，后台正在发送；您可以离开当前页面。" compact /> : working ? <LoadingState label="AI 正在生成英文回复草稿…" compact /> : null}
      {!sending && !working && inquiry.last_error && inquiry.status === "pending_review" ? (
        <div className="reply-validation-warning"><AlertTriangle size={17} /><div><strong>AI 处理提醒</strong><p>{inquiry.last_error}</p><small>询盘已保留，可人工编辑、重新生成或审核发送。</small></div></div>
      ) : inquiry.last_error && inquiry.status === "failed" ? <ErrorState compact message={inquiry.last_error} /> : null}
      {!sending && !working && reviewWarnings.length ? (
        <div className="reply-validation-warning"><AlertTriangle size={17} /><div><strong>{sent ? `自动回复已发送，保留 ${reviewWarnings.length} 项提醒` : `发送前请核对 ${reviewWarnings.length} 项`}</strong><ul>{reviewWarnings.slice(0, 5).map((item, index) => <li key={`${item?.type || "warning"}-${index}`}>{warningText(item)}</li>)}</ul><small>{sent ? "开启自动发送后，风险项只作页面记录，不会阻止有效客户询盘发送。" : "这些是审核提醒，不会拒绝保存草稿；点击发送即表示已完成人工审核。"}</small></div></div>
      ) : null}
      <div className="draft-editor">
        {draft || working ? (
          <TextArea
            value={draft}
            onChange={(event) => { setDraft(event.target.value); setDirty(true); }}
            placeholder="AI 回复草稿将在这里生成，也可以直接输入英文回复。"
            aria-label="英文回复草稿"
          />
        ) : <EmptyState icon={Sparkles} title="尚无回复草稿" description="生成前会参考客户原文、中文翻译和客户背调。" />}
      </div>
      {secondaryLoading ? <LoadingState label="正在读取 AI 回复依据…" compact /> : secondaryError ? <ErrorState compact title="回复依据暂未加载" message={secondaryError} /> : (inquiry.reply_knowledge_evidence || []).length ? (
        <details className="reply-knowledge-evidence">
          <summary><BookOpenCheck size={15} />查看 AI 回复依据 <small>{inquiry.reply_knowledge_evidence.length} 条已引用知识</small></summary>
          <div>
            <p className="reply-knowledge-evidence__note">以下为本次英文回复实际采用的已审核知识库资料，可核对引用内容与官方来源。</p>
            {inquiry.reply_knowledge_evidence.map((item) => (
              <article key={item.id}>
                <div className="reply-knowledge-evidence__title">
                  <strong>{item.source_title || item.product_name || "BLUESUN 官方资料"}</strong>
                  {item.product_category ? <span>{item.product_category}</span> : null}
                </div>
                {(item.matched_terms || []).length ? (
                  <div className="reply-knowledge-evidence__matches">
                    <small>回复匹配</small>
                    {item.matched_terms.map((term) => <span key={term}>{term}</span>)}
                  </div>
                ) : null}
                <p>{item.excerpt}</p>
                {/^https?:\/\//i.test(String(item.source_url || "")) ? <a href={item.source_url} target="_blank" rel="noreferrer">查看官方来源</a> : <small>BLUESUN 内部已审核产品资料</small>}
              </article>
            ))}
          </div>
        </details>
      ) : draft && !working ? (
        <div className="reply-knowledge-empty"><BookOpenCheck size={15} />本次回复未引用已审核的产品知识库资料</div>
      ) : null}
      <div className="revision-bar">
        <TextArea value={instruction} onChange={(event) => setInstruction(event.target.value)} placeholder="向 AI 提修改意见，例如：更简洁、只问两个问题、语气更真诚" />
        <Button icon={RefreshCw} loading={working} disabled={sending} onClick={() => onGenerate(instruction)}>按意见重新生成</Button>
      </div>
      <footer className="composer-actions">
        <Button icon={Clipboard} disabled={!draft.trim()} onClick={async () => {
          await navigator.clipboard.writeText(draft);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1800);
        }}>{copied ? "已复制" : "复制"}</Button>
        <Button icon={Save} loading={busy === "save"} disabled={!draft.trim()} onClick={async () => { await onSave(draft); setDirty(false); }}>保存草稿</Button>
        <Button variant="primary" icon={Send} disabled={sending || !draft.trim() || !recipients.length} onClick={() => onSend({ draft, recipients, cc })}>{sending ? "后台发送中" : reviewWarnings.length ? "确认审核并发送" : "审核并发送"}</Button>
      </footer>
    </section>
  );
}
